"use client";

import SignupButton from "@/components/shared/signup-button";
import { analyticsIdentifyUserIfNeeded } from "@/lib/analytics";
import cn from "@/lib/cn";
import { PRIVATE_DISCUSSION_BOARD_PATH } from "@/lib/constants";
import { formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { Dialog } from "@headlessui/react";
import { ArrowRightIcon, MenuIcon, XIcon } from "lucide-react";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import AnimatedButton from "./components/animated-button";
import NavLink from "./components/navlink";
import Notifications from "./components/notifications";
import CategoriesMenu from "./menus/categories-menu";
import CommunitiesMenu from "./menus/communities-menu";
import GrowWithUsMenu from "./menus/grow-with-us-menu";
import MediaMenu from "./menus/media-menu";
import SubmitProductMenu from "./menus/submit-product-menu";
import UserMenu from "./menus/user-menu";

type TNavLink = {
	href: string;
	title: string;
	isExternal?: boolean;
};

const NAVBAR_LINKS: TNavLink[] = [
	{
		title: "Events",
		href: "/events",
	},
	{
		title: "News",
		href: "/news",
	},
];

const MOBILE_NAVBAR_LINKS: TNavLink[] = [
	...NAVBAR_LINKS,
	{
		title: "Communities",
		href: PRIVATE_DISCUSSION_BOARD_PATH,
	},
	{
		title: "PTB Labs",
		href: "/ptb-labs",
	},
	{
		title: "US Goto Market",
		href: "/ptb-labs-us",
	},
	{
		title: "Venture Capital",
		href: "/vc",
	},
	{
		title: "Grow With Us",
		href: "http://growth.proptechbuzz.com/",
	},
	{
		title: "About Us",
		href: "/about-us",
	},
	{
		title: "How It Works",
		href: "/how-it-works",
	},

	{
		title: "Book a call?",
		href: "https://cal.com/rohit-sujan/discovery-call",
	},
	{
		title: "Contact Us",
		href: "https://www.proptechbuzz.com/contact-us",
	},
];

export const Navbar = () => {
	const { isLoggedIn, user } = useAuth();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const profilePictureUrl = formatImageUrl(user?.image) ?? "";
	const isVerified = user?.isVerified || false;
	const isFounder = user?.isFounder || false;
	const username = user?.username || "";
	const userId = user?.id;

	const userData = useMemo(
		() => ({
			userId: userId,
			name: user?.name,
			email: user?.email,
			username: username,
			isVerified: isVerified,
			isFounder: isFounder,
		}),
		[userId, user?.name, user?.email, username, isVerified, isFounder]
	);

	useEffect(() => {
		// Uses identify API to analyze actions across the site for the given user
		analyticsIdentifyUserIfNeeded({ isAuthenticated: isLoggedIn, userId, userData });
	}, [isLoggedIn, userId, userData]);

	const handleLogout = () => {
		// Reset analytics to clear any existing identifiers
		window.rudderanalytics?.reset();

		signOut();
	};

	return (
		<header className="sticky top-0 z-30 bg-white bg-opacity-60 backdrop-blur-lg backdrop-filter">
			<nav className="mx-auto flex max-w-7xl items-center justify-between gap-2 p-4 lg:px-4">
				<Link href="/" className="relative">
					<Image
						src="/images/proptechbuzz-logo.svg"
						alt="PropTechBuzz Logo"
						width={44}
						height={44}
						className="h-6 w-44 fill-[#271B58]"
					/>
				</Link>

				<div className="flex lg:hidden">
					<button
						type="button"
						className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
						onClick={() => setMobileMenuOpen(true)}
					>
						<span className="sr-only">Open main menu</span>
						<MenuIcon className="h-6 w-6" aria-hidden="true" />
					</button>
				</div>

				<div className="hidden lg:flex lg:items-center lg:gap-x-4">
					<div className="relative">
						<div>
							<span className="absolute -right-3 -top-3.5 z-0">
								<span className="animate-pulse rounded-lg bg-red-500 px-[5px] py-[1px] text-xxs text-white">
									NEW
								</span>
							</span>

							<NavLink title="Leaders Hive" href="/leaderboard" className="whitespace-nowrap" />
						</div>
					</div>

					<MediaMenu />

					<CategoriesMenu
						items={[
							{ name: "Intelligent Space Planning", url: "/categories/intelligent-space-planning" },
							{
								name: "Managed Wi-Fi Providers",
								url: "/categories/managed-wifi",
							},
							{ name: "Real Estate CRM", url: "/categories/real-estate-crm-portal" },
							{ name: "Fractional Investment", url: "/categories/fractional-investment" },
							{ name: "Visitor Management System", url: "/categories/visitor-management-system" },
							{ name: "DREA AWARDS 2025", url: "/categories/drea-awards" },
						]}
					/>

					<CommunitiesMenu />

					<NavLink title="VC Network" href="/vc" className="whitespace-nowrap" />
					<GrowWithUsMenu />

					<SubmitProductMenu />

					{!isLoggedIn ? <SignupButton size="md" /> : null}

					{isLoggedIn && <Notifications />}

					<UserMenu
						username={username}
						show={isLoggedIn}
						isVerified={isVerified}
						isFounder={isFounder}
						profileImageUrl={profilePictureUrl}
					/>
				</div>
			</nav>

			{/* Mobile nav links */}
			<Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
				<Dialog.Panel className="fixed inset-y-0 right-0 z-40 flex w-full flex-col overflow-y-auto bg-white p-4 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
					<div className="flex items-center justify-between">
						<Link href="/" onClick={() => setMobileMenuOpen(false)}>
							<Image
								src="/images/proptechbuzz-logo.svg"
								alt="PropTechBuzz Logo"
								width={44}
								height={44}
								className="h-6 w-44 fill-[#271B58]"
							/>
						</Link>

						<button
							type="button"
							className="-m-2.5 rounded-md p-2.5 text-gray-700"
							onClick={() => setMobileMenuOpen(false)}
						>
							<span className="sr-only">Close menu</span>
							<XIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					<div className="mt-6 flex-1">
						<div className="-my-6 flex h-full flex-col divide-y divide-gray-500/10">
							<div className="space-y-2 py-6">
								<NavLink
									title="Leaderboard"
									href="/leaderboard"
									onClick={() => setMobileMenuOpen(false)}
									className="mb-1 block p-2 text-base font-medium"
								/>

								{isVerified && username ? (
									<NavLink
										title="My Profile"
										href={`/${username}`}
										onClick={() => setMobileMenuOpen(false)}
										className="mb-1 block p-2 text-base font-medium text-gray-700"
									/>
								) : null}

								{isLoggedIn && (
									<NavLink
										title="Account"
										href="/account/profile"
										onClick={() => setMobileMenuOpen(false)}
										className="mb-1 block p-2 text-base font-medium text-gray-700"
									/>
								)}

								{MOBILE_NAVBAR_LINKS.map((item) => (
									<NavLink
										key={item.title}
										title={item.title}
										href={item.href}
										isExternal={item?.isExternal}
										onClick={() => setMobileMenuOpen(false)}
										className="mb-1 block p-2 text-base font-medium"
									/>
								))}
							</div>

							<div className="mt-auto space-y-3 px-2 pt-6">
								{!isLoggedIn ? (
									<Link
										href="/signin"
										onClick={() => setMobileMenuOpen(false)}
										className="block rounded border border-[#005EFF] px-4 py-3 text-center text-sm font-medium text-[#005EFF]"
									>
										Sign in
									</Link>
								) : (
									<button
										onClick={handleLogout}
										className="block w-full rounded border border-[#005EFF] px-4 py-3 text-center text-sm font-medium text-[#005EFF]"
									>
										Sign out
									</button>
								)}

								{!isVerified && isLoggedIn ? (
									<Link
										href="/account/profile"
										onClick={() => setMobileMenuOpen(false)}
										className={cn(
											"flex items-center justify-center gap-2 rounded bg-[#005EFF] px-4 py-3 text-sm font-semibold text-white transition",
											"hover:bg-blue-700 active:bg-blue-700"
										)}
									>
										Earn Your Verification Badge <ArrowRightIcon className="h-4 w-4 text-inherit" />
									</Link>
								) : null}

								<AnimatedButton />
							</div>
						</div>
					</div>
				</Dialog.Panel>
			</Dialog>
		</header>
	);
};
